// src/accounts/pages/AccountProfile.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../lib/api";

export default function AccountProfile() {
  const { user, refresh } = useAuth();

  const displayEmail = useMemo(() => String(user?.email || ""), [user?.email]);
  const displayUserName = useMemo(() => String(user?.name || ""), [user?.name]);

  const nameParts = useMemo(() => {
    const raw = String(user?.name || "").trim();
    if (!raw) return { first: "", last: "" };
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts.slice(0, -1).join(" "), last: parts.slice(-1).join(" ") };
  }, [user?.name]);

  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState({
    email: displayEmail,
    userName: displayUserName,
    firstName: nameParts.first,
    lastName: nameParts.last,
  });

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    // When user changes (login/refresh), reset the form (unless user is editing)
    if (isEditing) return;
    setProfile({
      email: displayEmail,
      userName: displayUserName,
      firstName: nameParts.first,
      lastName: nameParts.last,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayEmail, displayUserName, nameParts.first, nameParts.last, isEditing]);

  const setP = (k, v) => {
    setProfile((p) => ({ ...p, [k]: v }));
    if (err) setErr("");
    if (msg) setMsg("");
  };

  const setPW = (k, v) => {
    setPw((p) => ({ ...p, [k]: v }));
    if (err) setErr("");
    if (msg) setMsg("");
  };

  const cancelEdit = () => {
    setErr("");
    setMsg("");
    setIsEditing(false);
    setProfile({
      email: displayEmail,
      userName: displayUserName,
      firstName: nameParts.first,
      lastName: nameParts.last,
    });
  };

  const saveProfile = async () => {
    try {
      setErr("");
      setMsg("");

      const userName = String(profile.userName || "").trim();
      const firstName = String(profile.firstName || "").trim();
      const lastName = String(profile.lastName || "").trim();

      if (!userName) {
        setErr("Please enter a user name.");
        return;
      }
      if (!firstName && !lastName) {
        setErr("Please enter your first name and/or last name.");
        return;
      }

      setSavingProfile(true);

      // ✅ Email is NOT sent / NOT editable
      // ✅ Update username + first/last
      const { data } = await api.post("auth/update-profile", {
        userName,
        firstName,
        lastName,
      });

      if (!data?.ok) throw new Error(data?.message || "Failed to update profile.");

      setMsg("Profile updated.");
      setIsEditing(false);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Could not update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const onEditOrSave = async () => {
    if (!isEditing) {
      setErr("");
      setMsg("");
      setIsEditing(true);
      return;
    }
    await saveProfile();
  };

  const changePassword = async () => {
    try {
      setErr("");
      setMsg("");

      const current = String(pw.current || "");
      const next = String(pw.next || "");
      const confirm = String(pw.confirm || "");

      if (!current) {
        setErr("Please enter your current password.");
        return;
      }
      if (next.length < 8) {
        setErr("New password must be at least 8 characters.");
        return;
      }
      if (next !== confirm) {
        setErr("New password and confirmation do not match.");
        return;
      }

      setSavingPw(true);

      const { data } = await api.post("auth/change-password", {
        oldPassword: current,
        newPassword: next,
      });

      if (!data?.ok) throw new Error(data?.message || "Failed to change password.");

      setMsg("Password updated.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Could not change password.");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="accPage">
      <div className="accPageTitle">Profile</div>

      {err ? <div className="accAlert accAlertError">{err}</div> : null}
      {msg ? <div className="accAlert accAlertOk">{msg}</div> : null}

      <div className="accPanel">
        <div className="accForm">
       {/* Email — always disabled, NOT clickable */}
<div className="accField">
  <div className="accLabel">Email</div>
  <input
    className="accInput"
    value={displayEmail}
    disabled
  />
</div>

{/* Username — clickable when NOT editing */}
<div className="accField">
  <div className="accLabel">Username</div>

  {!isEditing ? (
    <a
      href={`https://mychinesehoroscope.com/${profile.userName}`}
      target="_blank"
      rel="noopener noreferrer"
      className="accInput"
      style={{
        display: "block",
        textDecoration: "none",
        cursor: "pointer"
      }}
    >
      {profile.userName || "—"}
    </a>
  ) : (
    <input
      className="accInput"
      value={profile.userName}
      onChange={(e) => setP("userName", e.target.value)}
      placeholder="Username"
    />
  )}
</div>

          <div className="accGrid2">
            {/* ✅ First/Last: not editable until Edit clicked */}
            <div className="accField">
              <div className="accLabel">First name</div>
              <input
                className="accInput"
                value={profile.firstName}
                disabled={!isEditing}
                onChange={(e) => setP("firstName", e.target.value)}
                placeholder="First name"
              />
            </div>

            <div className="accField">
              <div className="accLabel">Last name</div>
              <input
                className="accInput"
                value={profile.lastName}
                disabled={!isEditing}
                onChange={(e) => setP("lastName", e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          {/* ✅ Button toggles Edit -> Save */}
          <button
            type="button"
            className="accBtn accBtnPrimarySub accBtnFull"
            onClick={onEditOrSave}
            disabled={savingProfile}
          >
            {savingProfile ? "Saving..." : isEditing ? "Save profile" : "Edit"}
          </button>

          {/* Optional Cancel button (only visible while editing) */}
          {isEditing ? (
            <button
              type="button"
              className="accBtn accBtnFull"
              onClick={cancelEdit}
              disabled={savingProfile}
              style={{ marginTop: 10 }}
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ height: 16 }} />

      <div className="accPanel">
        <div className="accPageTitle" style={{ fontSize: 28, margin: "0 0 10px" }}>
          Change password
        </div>

        <div className="accForm">
          <div className="accField">
            <div className="accLabel">Current password</div>
            <input
              className="accInput"
              type="password"
              value={pw.current}
              onChange={(e) => setPW("current", e.target.value)}
              placeholder="Current password"
              autoComplete="current-password"
            />
          </div>

          <div className="accField">
            <div className="accLabel">New password</div>
            <input
              className="accInput"
              type="password"
              value={pw.next}
              onChange={(e) => setPW("next", e.target.value)}
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
            />
          </div>

          <div className="accField">
            <div className="accLabel">Confirm new password</div>
            <input
              className="accInput"
              type="password"
              value={pw.confirm}
              onChange={(e) => setPW("confirm", e.target.value)}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </div>

          <button
            type="button"
            className="accBtn accBtnPrimarySub accBtnFull"
            onClick={changePassword}
            disabled={savingPw}
          >
            {savingPw ? "Updating..." : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}